import React, {useCallback, useContext, useMemo, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';

import {clearSelection} from 'src/components/data-table-selection/services/data-table-selection-slice';
import {AdminSortField as SortFields} from 'src/openapi/admin/models/AdminSortField';
import {SortDirection as SortDirections} from 'src/openapi/admin/models/SortDirection';

import {SortHeaderFilter} from '@pb/kit-component-filters';
import {LocaleContext} from '@pb/kit-context-locale';


import {SELECTION_KEY} from '../../constants';
import {assignedUsersResultSelector} from '../../slices/assigned-users-slice';
import {
    assignedUsersStateActions,
    assignedUsersStateSelector,
} from '../../slices/assigned-users-state-slice';
import {getHeader} from '../../utils';

import {EmailFilter} from './email.filter';

import type {SortDirection} from 'src/openapi/admin/models/SortDirection';

export const EmailHeader = () => {
    const dispatch = useDispatch();
    const cacheKeys = useSelector(assignedUsersStateSelector);
    const result = useSelector(assignedUsersResultSelector);

    const [email, setEmail] = useState(cacheKeys.filters.email as string || undefined);

    const {locale} = useContext(LocaleContext);

    const sort = useCallback((direction: ('ASC' | 'DESC')) => {
        const sortCriteria = {
            direction: direction as SortDirection,
            field: SortFields.EMAIL,
        };

        dispatch(clearSelection(SELECTION_KEY));
        dispatch(assignedUsersStateActions.setSortCriteria(sortCriteria));
    }, [dispatch]);

    const applyFilter = useCallback(() => {
        dispatch(clearSelection(SELECTION_KEY));
        if (email) {
            dispatch(assignedUsersStateActions.setFilters({email}));
        } else {
            dispatch(assignedUsersStateActions.setFilters({}));
        }
    }, [email, dispatch]);

    const clearFilter = useCallback(() => {
        dispatch(clearSelection(SELECTION_KEY));
        setEmail(undefined);
        dispatch(assignedUsersStateActions.setFilters({}));
    }, [dispatch]);

    const caption = useMemo(() => getHeader(locale, result?.totalCount), [result?.totalCount, locale]);

    return (
        <SortHeaderFilter
            filterActive={!!cacheKeys.filters.email}
            sortable={true}
            caption={caption}
            direction={cacheKeys.sortCriteria.field === SortFields.EMAIL
                ? cacheKeys.sortCriteria.direction
                : undefined}
            defaultDirection={SortDirections.ASC}
            onSort={sort}
            onApply={applyFilter}
            onReset={clearFilter}
            onCancel={() => setEmail(cacheKeys.filters.email as string || undefined)}>
            <EmailFilter email={email} onChange={setEmail}/>
        </SortHeaderFilter>
    );
};
